import { useMemo } from "react";
import { useStoryblokProxy } from "./useStoryblokProxy";
import type { ISbStoryData } from "@storyblok/react";

/**
 * Resolve Storyblok UUIDs (e.g. brands or categories) to their story names.
 */
export const useStoryblokStoryNames = (
  uuids: (string | null | undefined)[]
) => {
  // Drop empty/duplicate uuids so callers can pass e.g. [brandParam, categoryParam]
  // directly without checking for null first, and so the fetch below is deduped.
  const unique = useMemo(
    () => Array.from(new Set(uuids.filter((uuid): uuid is string => !!uuid))),
    [uuids]
  );

  // Resolve all uuids in a single request (Storyblok's `by_uuids` accepts a
  // comma-separated list) instead of one request per uuid. Disabled entirely
  // when there's nothing to resolve.
  const { data, isLoading, isError } = useStoryblokProxy(
    unique.length
      ? { by_uuids: unique.join(","), per_page: unique.length }
      : { by_uuids: "" },
    unique.length === 0
  );

  // Index the resolved stories by uuid so callers can look up each name directly.
  const map = useMemo(() => {
    const result = new Map<string, string>();
    const stories = (data?.stories ?? []) as unknown as ISbStoryData[];
    stories.forEach((s) => {
      if (s.uuid && s.name) {
        result.set(s.uuid, s.name);
      }
    });
    return result;
  }, [data]);

  return { map, isLoading, isError };
};
